import { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  alpha,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme,
} from '@mui/material';
import _ from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { RHFSelect } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/FormProvider';
import Iconify from 'src/components/iconify';
import { HEADER } from 'src/config-global';
import { IUnifiedFilter, useSearchFilterContext } from 'src/contexts/SearchFilterContext';
import { useStores } from 'src/models';
import { isMobile } from 'src/utils/common';

import { FilterSection } from './FilterSection';
import { TooltipContents } from './FilterTooltip';
import { KeywordPortal } from './KeywordPortal';

export type FilterFormType = IUnifiedFilter;

export interface FilterPageConfig {
  sections: FilterSectionKey[];
  defaultValues: Partial<IUnifiedFilter>;
  title: string;
  info: string[];
  customFields?: string[];
}

export type FilterSectionKey = 'order' | 'sales' | 'keyword' | 'common';

// ============= Constants =============
const LOST_CODE = 200307;
const CURRENT_YEAR = new Date().getFullYear();

// ============= Helper Functions =============

/**
 * SessionStorage에서 필터값 가져오기
 */
const getStoredFilter = (key: string): IUnifiedFilter | undefined => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * 코드 기본값 추출 함수
 */
const getCodeDefaults = (codeStore: any) => {
  if (!codeStore?.commonCodes?.length) return {};

  const NEW_STATUS = codeStore.getCodeByName('NEW_STATUS_CD') || [];
  const ONGOING = codeStore.getCodeByName('ONGOING_CD') || [];
  const BZ_STEP =
    codeStore
      .getCodeByName('BZ_STEP_CD')
      .filter(
        (v: { value: number | string; label: string }) => String(v.value) !== String(LOST_CODE),
      ) || [];
  const SIGN = codeStore.getCodeByName('SIGN_CD') || [];

  return {
    NEW_STATUS,
    ONGOING,
    SIGN,
    newStatusAll: NEW_STATUS.map((v: { value: number | string; label: string }) => String(v.value)),
    ongoingAll: ONGOING.map((v: { value: number | string; label: string }) => String(v.value)),
    bzStepAll: BZ_STEP.map((v: { value: number | string; label: string }) => v.value),
    firstSign: SIGN[0]?.value || '',
  };
};

/**
 * 페이지별 필터 설정 생성
 */
const createPageConfigs = (
  codeStore?: any,
  searchParams?: URLSearchParams,
): Record<string, FilterPageConfig> => {
  const codes = getCodeDefaults(codeStore);

  return {
    '/fc-chart': {
      title: 'FC Chart',
      info: [
        'Order/Sales 차트: 기준연도, 기저/신규, 스팟/지속, 영업단계, 수주확률로 필터링',
        'Purchase 차트: 기준연도만 적용',
      ],
      sections: ['order', 'sales'],
      defaultValues: {
        targetYear: CURRENT_YEAR, // ✅ 라이브 모드 기본값
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,

        salesNewStatusCdList: codes.newStatusAll,
        salesOngoingCdList: codes.ongoingAll,
        noSalesBzStepCd: true,
        salesBzStepCdList: codes.bzStepAll,
        salesPrcnt: '',
        salesSign: codes.firstSign,
      },
    },

    '/project': {
      title: 'Project',
      info: ['프로젝트를 기준연도, 기저/신규, 스팟/지속, 영업단계, 수주확률, 키워드로 필터링'],
      sections: ['common', 'keyword'],
      defaultValues: {
        targetYear: '',
        newStatusCdList: codes.newStatusAll,
        ongoingCdList: codes.ongoingAll,
        noBzStepCd: true,
        bzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,
        text: '',
      },
    },

    '/dashboard/margin': {
      title: 'Dashboard(MARGIN)',
      info: [
        'Order 차트: 기준연도, 기저/신규, 스팟/지속, 영업단계, 수주확률로 필터링',
        'Margin 차트: 기준연도만 적용',
      ],
      sections: ['order'],
      defaultValues: {
        targetYear: CURRENT_YEAR,
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,
      },
    },
    '/dashboard/size': {
      title: 'Dashboard(SIZE)',
      sections: ['order', 'sales'],
      info: [
        'Order/Sales 차트: 기준연도, 기저/신규, 스팟/지속, 영업단계, 수주확률로 필터링',
        'Purchase 차트: 기준연도만 적용',
      ],
      defaultValues: {
        targetYear: CURRENT_YEAR,
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,

        salesNewStatusCdList: codes.newStatusAll,
        salesOngoingCdList: codes.ongoingAll,
        noSalesBzStepCd: true,
        salesBzStepCdList: codes.bzStepAll,
        salesPrcnt: '',
        salesSign: codes.firstSign,
      },
    },
    '/dashboard/speed': {
      title: 'Dashboard(SPEED)',
      sections: [],
      info: ['SPEED 차트를 기준연도로 필터링'],
      defaultValues: {
        targetYear: CURRENT_YEAR,
      },
    },
    '/dashboard/fs': {
      title: 'Dashboard(SF)',
      sections: [],
      info: ['Success/Fail 차트를 기준연도로 필터링'],
      defaultValues: {
        targetYear: CURRENT_YEAR,
      },
    },
  };
};

/**
 * filter data 정제
 */
function cleanFilterData(data: IUnifiedFilter, isSnapshot: boolean): IUnifiedFilter {
  // ✅ isSnapshot이면 미리 targetYear 제거
  if (isSnapshot) {
    data = _.omit(data, ['targetYear']);
  }

  const formatted = _.mapValues(data, (value) =>
    Array.isArray(value) ? value.join(',') : value || '',
  );

  const filtered = _.omit(formatted, ['noBzStepCd', 'noOrderBzStepCd', 'noSalesBzStepCd']);
  const pureFilter = _.pickBy(
    filtered,
    (value) => value !== '' && value !== undefined && value !== null,
  );

  // ✅ targetYear는 빈 문자열이어도 유지 (프로젝트 페이지에서 "전체" 조회 시 필요)
  if ('targetYear' in data && !isSnapshot) {
    pureFilter.targetYear = formatted.targetYear ?? '';
  }

  if (formatted.noBzStepCd) delete pureFilter.bzStepCdList;
  if (formatted.noOrderBzStepCd) delete pureFilter.orderBzStepCdList;
  if (formatted.noSalesBzStepCd) delete pureFilter.salesBzStepCdList;
  if (!formatted.orderPrcnt) {
    delete pureFilter.orderPrcnt;
    delete pureFilter.orderSign;
  }
  if (!formatted.salesPrcnt) {
    delete pureFilter.salesPrcnt;
    delete pureFilter.salesSign;
  }

  return pureFilter;
}

// ============= Custom Hooks =============

/**
 * 페이지 설정 및 초기화 관련 로직
 */
const useFilterConfig = () => {
  const rootStore = useStores();
  const { commonCodeStore: codeStore } = rootStore;
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pathname = location.pathname === '/' ? '/fc-chart' : location.pathname;
  const commonCodes = codeStore.commonCodes;
  const isCodesLoaded = !!commonCodes.length;

  const pageConfigs = useMemo(
    () => createPageConfigs(codeStore, searchParams),
    [commonCodes.length, searchParams],
  );

  const currentConfig = useMemo(() => {
    return pageConfigs[pathname];
  }, [pathname, pageConfigs]);

  const isSnapshot = currentConfig?.title === 'FC Chart' && !!searchParams.get('snap');

  const YEAR_LIST = useMemo(() => {
    // console.log('🔍 YEAR_LIST 재계산:', {
    //   isCodesLoaded,
    //   length: codeStore?.targetYearRange?.length,
    //   첫번째값: codeStore?.targetYearRange?.[0],
    // });

    if (!isCodesLoaded) return [];

    const yearRange = toJS(codeStore?.targetYearRange || []);

    if (!yearRange.length) return [];

    if (isSnapshot) return yearRange;
    if (currentConfig?.title === 'FC Chart') return yearRange.filter(Boolean);
    if (currentConfig?.defaultValues?.targetYear) return yearRange.filter(Boolean);

    return yearRange;
  }, [
    codeStore?.targetYearRange?.length,
    currentConfig?.title,
    currentConfig?.defaultValues?.targetYear,
    isSnapshot,
    isCodesLoaded,
  ]);
  return {
    pathname,
    currentConfig,
    isSnapshot,
    isCodesLoaded,
    codeStore,
    YEAR_LIST,
  };
};

/**
 * 초기 필터값 계산
 */
const useInitialFilter = (
  currentConfig: FilterPageConfig | undefined,
  isSnapshot: boolean,
  isCodesLoaded: boolean,
  pathname: string,
) => {
  const FILTER_SESSION_KEY = pathname === '/' ? 'filter_/fc-chart' : `filter_${pathname}`;

  return useMemo((): IUnifiedFilter => {
    if (!isCodesLoaded || !currentConfig) return {};

    const storedFilter = getStoredFilter(FILTER_SESSION_KEY);

    // ✅ sessionStorage 우선
    if (storedFilter) {
      // Snapshot 모드: targetYear 제거
      if (isSnapshot) {
        const { targetYear, ...otherFilter } = storedFilter;
        return otherFilter;
      }
      return storedFilter;
    }

    // ✅ sessionStorage 없으면 defaultValues 사용
    const baseFilter: IUnifiedFilter = (currentConfig?.defaultValues as IUnifiedFilter) || {};

    // Snapshot 모드: targetYear 제거
    if (isSnapshot) {
      const { targetYear, ...otherFilter } = baseFilter;
      return otherFilter;
    }

    return baseFilter;
  }, [currentConfig, isSnapshot, isCodesLoaded, FILTER_SESSION_KEY]);
};

// ============= Styled Components =============

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.neutral,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.neutral,
  },
}));

// ============= Main Component =============

export const SearchFilter = observer(() => {
  const { executeCallback, setFilter, openFilter, setOpenFilter } = useSearchFilterContext();
  const theme = useTheme();

  // Custom hooks로 로직 분리
  const { pathname, currentConfig, isSnapshot, isCodesLoaded, codeStore, YEAR_LIST } =
    useFilterConfig();

  useEffect(() => {
    const loadYearRange = async () => {
      if (!codeStore.targetYearRange?.length) {
        // console.log('targetYearRange 로딩 시도...');
        await codeStore.getsTargetYearRange(); // 또는 적절한 API 호출
      }
    };
    loadYearRange();
  }, [codeStore]);

  const initialFilter = useInitialFilter(currentConfig, isSnapshot, isCodesLoaded, pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // 마지막으로 제출(저장)된 필터값을 추적
  const lastAppliedFilterRef = useRef<IUnifiedFilter | undefined>(undefined);

  const FILTER_SESSION_KEY = pathname === '/' ? 'filter_/fc-chart' : `filter_${pathname}`;

  // ✅ 필터 사용 가능 여부 (원본 로직)
  const isFilterAvailable = useMemo(() => {
    const explicitFilterPages = ['/fc-chart', '/project', '/dashboard/margin', '/dashboard/size'];

    // 명시적 목록에 있으면 우선 처리
    if (explicitFilterPages.includes(pathname)) {
      // project 페이지는 URL parameter 체크
      if (pathname === '/project') {
        const params = Object.fromEntries(
          Array.from(new URLSearchParams(location.search).entries()).filter(
            ([, value]) => value && value !== 'null' && value !== 'undefined',
          ),
        );
        const hasUrlParams = Object.keys(params).length > 0;
        return !hasUrlParams; // URL parameter가 없을 때만 필터 사용 가능
      }
      return true;
    }

    // 기존 로직 (pageConfigs 기반)
    const pageConfigs = createPageConfigs(codeStore, searchParams);
    const hasPageConfig = Object.keys(pageConfigs).includes(pathname);
    return hasPageConfig && isCodesLoaded;
  }, [pathname, location.search, isCodesLoaded, codeStore, searchParams]);

  // ============= Form Setup =============
  const methods = useForm<IUnifiedFilter>({
    // defaultValues: {}, // ✅ 빈 객체로 시작 (YEAR_LIST 로드 전)
    defaultValues: useMemo(() => {
      if (!isCodesLoaded) {
        // 코드 로드 전: 모든 필드에 빈 문자열 또는 빈 배열
        return {
          targetYear: '',
          orderNewStatusCdList: [],
          orderOngoingCdList: [],
          // ... 다른 필드들
        };
      }
      return initialFilter;
    }, [isCodesLoaded, initialFilter]),
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    // formState: { isDirty },
  } = methods;

  // ============= Helper: 모든 가능한 필드 기본값 =============
  const getAllPossibleFields = useCallback((): IUnifiedFilter => {
    const codes = getCodeDefaults(codeStore);
    return {
      targetYear: CURRENT_YEAR, // ✅ 기본값 설정
      // Order 관련
      orderNewStatusCdList: codes.newStatusAll ?? [],
      orderOngoingCdList: codes.ongoingAll ?? [],
      noOrderBzStepCd: true,
      orderBzStepCdList: codes.bzStepAll ?? [],
      orderPrcnt: '',
      orderSign: codes.firstSign,
      // Sales 관련
      salesNewStatusCdList: codes.newStatusAll ?? [],
      salesOngoingCdList: codes.ongoingAll ?? [],
      noSalesBzStepCd: true,
      salesBzStepCdList: codes.bzStepAll ?? [],
      salesPrcnt: '',
      salesSign: codes.firstSign,
      // Project 관련
      newStatusCdList: codes.newStatusAll ?? [],
      ongoingCdList: codes.ongoingAll ?? [],
      noBzStepCd: true,
      bzStepCdList: codes.bzStepAll ?? [],
      text: '',
    };
  }, [codeStore]);

  // ============= Form 동기화 =============

  // ✅ 1순위: 코드 로드 시 초기 필터값 설정 (가장 먼저 실행)
  useEffect(() => {
    if (isCodesLoaded && currentConfig) {
      const initialFilterValue = initialFilter;
      reset(initialFilterValue, { keepDefaultValues: false });
      lastAppliedFilterRef.current = initialFilterValue;

      const cleanedData = cleanFilterData(initialFilterValue, isSnapshot);
      setFilter(cleanedData);
    }
  }, [isCodesLoaded, pathname]);

  // ✅ isSnapshot이면 targetYear를 "선택안함"으로 설정
  useEffect(() => {
    if (isSnapshot && YEAR_LIST.length > 0) {
      setValue('targetYear', '', { shouldDirty: false });
    }
  }, [isSnapshot, YEAR_LIST.length, setValue]);

  // ✅ searchParams 변경 시 form reset
  useEffect(() => {
    if (isCodesLoaded && currentConfig) {
      reset(initialFilter);
    }
  }, [isCodesLoaded, searchParams.toString(), currentConfig]);

  // ✅ 페이지 변경 시 완전 초기화 및 자동 데이터 로드 (가장 중요!)
  useEffect(() => {
    if (!isCodesLoaded) return;

    // 1. 모든 필드를 기본값으로 완전 리셋
    const allFields = getAllPossibleFields();
    reset(allFields, {
      keepDefaultValues: false,
      keepValues: false,
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false,
    });

    // 2. 새 페이지의 초기값 설정 및 자동 데이터 로드
    setTimeout(() => {
      const initialFilterValue = initialFilter;

      // 3. 각 필드를 명시적으로 설정
      Object.entries(initialFilterValue).forEach(([key, value]) => {
        setValue(key as keyof IUnifiedFilter, value, {
          shouldValidate: false,
          shouldDirty: false,
        });
      });

      lastAppliedFilterRef.current = initialFilterValue;
      const cleanedData = cleanFilterData(initialFilterValue, isSnapshot);
      setFilter(cleanedData);

      // ✅ 자동 데이터 로드 (단, /project에서 search params가 있으면 제외)
      if (!(pathname === '/project' && location.search)) {
        executeCallback(cleanedData);
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isCodesLoaded]); // pathname 변경 시마다 실행

  // ✅ YEAR_LIST 로드 후 targetYear 검증 및 동기화
  useEffect(() => {
    if (!isCodesLoaded || YEAR_LIST.length === 0) return;

    const currentYear = getValues('targetYear');

    // targetYear가 설정되어 있고, YEAR_LIST에 없는 경우
    if (currentYear) {
      const yearExists = YEAR_LIST.some((year) => year && Number(year) === Number(currentYear));

      if (!yearExists) {
        // 리스트에 없으면 첫 번째 유효한 값 또는 빈 값으로 설정
        const firstValidYear = YEAR_LIST.find((year: any) => year !== null && year !== '');
        setValue('targetYear', firstValidYear ? Number(firstValidYear) : '', {
          shouldDirty: false,
        });
      }
    }
  }, [YEAR_LIST, isCodesLoaded, getValues, setValue]);

  // ✅ 페이지 변경 시 필터 open/close 상태 관리
  useEffect(() => {
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }

    const timer = setTimeout(() => {
      if (isFilterAvailable) {
        const last = sessionStorage.getItem('lastFilterOpen');
        if (last === 'true') setOpenFilter(true);
      } else {
        setOpenFilter(false);
        if (lastAppliedFilterRef.current) {
          reset(lastAppliedFilterRef.current);
        }
      }
    }, 50); // 50ms 지연 : dashboard filter 닫힘현상

    return () => clearTimeout(timer);
  }, [pathname, isFilterAvailable, reset, setOpenFilter]);

  // ============= Event Handlers =============

  const onOpenFilter = useCallback(() => {
    setOpenFilter(true);
    sessionStorage.setItem('lastFilterOpen', 'true');
    // 필터가 열릴 때 마지막으로 적용된 값으로 복원
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }
  }, [reset, setOpenFilter]);

  const onCloseFilter = useCallback(() => {
    setOpenFilter(false);
    sessionStorage.setItem('lastFilterOpen', 'false');
    // 마지막으로 저장된 값으로 복원
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }
  }, [reset, setOpenFilter]);

  const onSubmit = async (data: IUnifiedFilter) => {
    // snapshot 모드 해제 여부 판단
    const willExitSnapshotMode = isSnapshot && data.targetYear;

    // ✅ Snapshot 모드에서 targetYear를 선택하면 snapshot 모드 해제
    if (willExitSnapshotMode) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('snap');
      setSearchParams(newSearchParams, { replace: true });
    }

    // ✅ snapshot 모드를 해제하는 경우에는 targetYear 포함, 유지하는 경우에는 제외
    const dataToStore = isSnapshot && !willExitSnapshotMode ? _.omit(data, ['targetYear']) : data;

    const defaultFilter =
      isSnapshot && !willExitSnapshotMode
        ? _.omit(currentConfig?.defaultValues || getAllPossibleFields(), ['targetYear'])
        : currentConfig?.defaultValues || getAllPossibleFields();

    // default와 동일한 경우 sessionStorage 제거
    if (_.isEqual(dataToStore, defaultFilter)) {
      sessionStorage.removeItem(FILTER_SESSION_KEY);
      lastAppliedFilterRef.current = dataToStore;
    } else {
      // 변경된 값만 sessionStorage 저장
      sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify(dataToStore));
      lastAppliedFilterRef.current = dataToStore;
    }

    // ✅ targetYear를 선택한 경우, cleanFilterData에 포함
    const shouldIncludeTargetYear = isSnapshot && data.targetYear;
    const cleanedData = cleanFilterData(data, isSnapshot && !shouldIncludeTargetYear);

    // ✅ filter 상태 업데이트 (DesktopScreenProjects와 동기화)
    setFilter(cleanedData);

    await executeCallback(cleanedData);
  };

  const handleReset = useCallback(() => {
    if (!isCodesLoaded) return;
    const defaultFilter = currentConfig?.defaultValues ?? getAllPossibleFields();
    reset(defaultFilter);
  }, [currentConfig, isCodesLoaded, reset, getAllPossibleFields]);

  // ============= Render Helpers =============

  // const FilterInfo = () => {
  //   if (!currentConfig) return null;

  //   return (
  //     <Stack gap={1} p={2}>
  //       <Typography variant="h6" gutterBottom>
  //         필터 안내
  //       </Typography>
  //       <Divider />
  //       <List dense>
  //         {currentConfig.info.map((text, idx) => (
  //           <ListItem key={idx}>
  //             <Typography variant="body1">• {text}</Typography>
  //           </ListItem>
  //         ))}
  //         {currentConfig?.sections?.includes('keyword') && (
  //           <ListItem>
  //             <Typography variant="body1">
  //               • 키워드로 검색 가능한 항목은 프로젝트명, 고객사, 계약사, ESM 코드, UUID,
  //               사업관리코드, 사업기안번호 입니다.
  //             </Typography>
  //           </ListItem>
  //         )}
  //       </List>
  //     </Stack>
  //   );
  // };
  const FilterInfo = () => {
    return (
      <Stack p={1}>
        <Typography variant="subtitle1">{currentConfig?.title} 사용 안내</Typography>
        <List sx={{ '& .MuiListItem-root': { px: 0 } }}>
          {currentConfig?.info?.map((v, i) => (
            <ListItem key={'list-item' + i}>
              <Typography variant="body1">• {v}</Typography>
            </ListItem>
          ))}
          {currentConfig?.sections &&
            currentConfig.sections.length > 0 &&
            (currentConfig.sections.includes('common') ||
              currentConfig.sections.includes('order') ||
              currentConfig.sections.includes('sales')) && (
              <>
                <Divider sx={{ my: 2 }} />
                <ListItem>
                  <Typography variant="body1">
                    • 신규/기저, 스팟/지속: 한 개 이상 선택 필요
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    • 영업단계: '선택안함' 기본, 해제 시 다중 선택 가능
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    • 수주확률: '선택안함' 기본, 선택 시 Higher(이상)/Equal(같음)/Lower(이하) 선택
                    가능
                  </Typography>
                </ListItem>
              </>
            )}
          {currentConfig?.sections && currentConfig.sections.includes('keyword') && (
            <ListItem>
              <Typography variant="body1">
                • 키워드로 검색 가능한 항목은 프로젝트명, 고객사, 계약사, ESM 코드, UUID,
                사업관리코드, 사업기안번호 입니다.
              </Typography>
            </ListItem>
          )}
        </List>
      </Stack>
    );
  };

  // ============= Render =============

  const drawerRoot = document.getElementById('drawer-root');

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} formId="chart-filter">
      {/* 키워드 검색 Portal - FormProvider 내부, 항상 렌더링 */}
      {pathname === '/project' && <KeywordPortal onSubmit={handleSubmit(onSubmit)} />}

      <IconButton
        onClick={openFilter ? onCloseFilter : onOpenFilter}
        disabled={!isFilterAvailable}
        sx={{ width: 42 }}
      >
        <LightTooltip
          title={
            currentConfig &&
            !openFilter && <TooltipContents currentValues={watch()} layout={currentConfig} />
          }
          arrow
        >
          {isFilterAvailable ? (
            <Iconify icon={'mage:filter-fill'} color={theme.palette.primary.main} />
          ) : (
            <Iconify icon={'mage:filter'} />
          )}
        </LightTooltip>
      </IconButton>

      {/* Drawer를 Portal로 */}
      {drawerRoot &&
        ReactDOM.createPortal(
          <Drawer
            open={openFilter}
            onClose={onCloseFilter}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                scrollbarWidth: 'thin',
                width: isMobile() ? 375 : 400,
                flexShrink: 0,
                pt: `${HEADER.H_MAIN_DESKTOP}px`,
                zIndex: theme.zIndex.appBar - 1,
                [`& .MuiDrawer-paper`]: { boxSizing: 'border-box', overflow: 'auto' },
              },
            }}
          >
            {openFilter && currentConfig && (
              <>
                {/* Header */}
                <Stack
                  direction="row"
                  position="sticky"
                  justifyContent="space-between"
                  alignItems={'center'}
                  top={0}
                  p={2}
                  sx={{
                    zIndex: theme.zIndex.appBar,
                    background: `linear-gradient(0deg, ${alpha(
                      theme.palette.background.paper,
                      0,
                    )} 0%, ${theme.palette.background.paper} 50%)`,
                  }}
                >
                  <Typography variant="h6">{currentConfig.title}</Typography>

                  <Stack direction="row" right={0} justifyContent="flex-end">
                    <Tooltip arrow title={FilterInfo()}>
                      <Iconify
                        icon={'material-symbols:info-outline-rounded'}
                        m={1}
                        color={theme.palette.info.main}
                      />
                    </Tooltip>
                    <IconButton onClick={onCloseFilter}>
                      <Iconify icon="mdi:close" />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Form */}
                <Stack gap={3}>
                  <Stack gap={1} px={2}>
                    {/* 기준연도 Select */}
                    <Typography variant="body1">기준연도</Typography>
                    {/* {!isCodesLoaded ? (
                        <Skeleton variant="rectangular" width="50%" height={40} />
                      ) : (
                        // // YEAR_LIST 로드 후에만 렌더링
                        YEAR_LIST && (
                          <RHFSelect
                            name="targetYear"
                            label="년도"
                            size="small"
                            variant="outlined"
                            sx={{ width: '50%' }}
                          >
                            {YEAR_LIST.map((year, i) => {
                              const numValue = year ? Number(year) : null;
                              return (
                                <MenuItem value={numValue ?? ''} key={'year-' + i}>
                                  {year
                                    ? year
                                    : `선택안함${
                                        isSnapshot
                                          ? ''
                                          : currentConfig.title === 'Project'
                                          ? ' (전체)'
                                          : ''
                                      }`}
                                </MenuItem>
                              );
                            })}
                          </RHFSelect>
                        )
                      )} */}

                    {!isCodesLoaded || YEAR_LIST.length === 0 ? (
                      <Skeleton variant="rectangular" width="50%" height={40} />
                    ) : (
                      <RHFSelect
                        name="targetYear"
                        label="년도"
                        size="small"
                        variant="outlined"
                        sx={{ width: '50%' }}
                      >
                        {YEAR_LIST.map((year, i) => {
                          const numValue = year ? Number(year) : null;
                          return (
                            <MenuItem value={numValue ?? ''} key={'year-' + i}>
                              {year
                                ? year
                                : `선택안함${
                                    isSnapshot
                                      ? ''
                                      : currentConfig.title === 'Project'
                                      ? ' (전체)'
                                      : ''
                                  }`}
                            </MenuItem>
                          );
                        })}
                      </RHFSelect>
                    )}

                    {/* Filter Sections */}
                    {isCodesLoaded && currentConfig ? (
                      currentConfig.sections.map((section) => {
                        return (
                          <FilterSection
                            key={section}
                            section={section}
                            color={
                              section === 'order' || section === 'common'
                                ? theme.palette.primary.light
                                : theme.palette.secondary.main
                            }
                            control={control}
                            watch={watch}
                            codeStore={codeStore}
                            pathname={pathname}
                            onSubmit={handleSubmit(onSubmit)}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" align="center" sx={{ py: 4 }}>
                        필터 옵션을 불러오는 중...
                      </Typography>
                    )}
                  </Stack>

                  {/* Footer Buttons */}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1}
                    position="sticky"
                    bottom={0}
                    p={2}
                    sx={{
                      zIndex: theme.zIndex.appBar,
                      background: `linear-gradient(0deg, ${
                        theme.palette.background.paper
                      } 50%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
                    }}
                  >
                    <IconButton onClick={handleReset}>
                      <Iconify icon={'material-symbols:refresh'} />
                    </IconButton>

                    <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                      저장 및 조회
                    </Button>
                  </Stack>
                </Stack>
              </>
            )}
          </Drawer>,
          document.getElementById('drawer-root') as HTMLElement,
        )}
    </FormProvider>
  );
});

export default SearchFilter;
